import { useEffect, useMemo, useState } from "react";
import { Loader2, X, Play, ExternalLink, Search, Flame } from "lucide-react";
import { api, PLATFORM_LINKS } from "@/lib/api";
import PodcastRow from "@/components/PodcastRow";
import EpisodeCard from "@/components/EpisodeCard";
import ShareEpisode from "@/components/ShareEpisode";
import ShowsGrid from "@/components/ShowsGrid";

const CATEGORY_ORDER = [
  "Nouveautés",
  "Séries de messages",
  "Vie de foi",
  "Études bibliques",
  "Couple & Famille",
  "Tous les épisodes",
];

const CATEGORY_SUBTITLES = {
  "Nouveautés": "Les derniers épisodes chauds du micro",
  "Séries de messages": "Des enseignements en plusieurs volets",
  "Vie de foi": "Prière, grâce, témoignages",
  "Études bibliques": "Plonge dans la Parole en profondeur",
  "Couple & Famille": "Vivre l'Évangile à la maison",
  "Tous les épisodes": "Toute la bibliothèque",
};

export default function Podcasts() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("Tout");

  useEffect(() => {
    api
      .get("/podcasts/episodes")
      .then((r) => setEpisodes(r.data.episodes || []))
      .catch((e) => setError(e?.response?.data?.detail || "Impossible de charger le flux"))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    const map = {};
    for (const ep of episodes) {
      (map[ep.category] = map[ep.category] || []).push(ep);
    }
    if (!map["Tous les épisodes"] || map["Tous les épisodes"].length < episodes.length) {
      map["Tous les épisodes"] = episodes;
    }
    return CATEGORY_ORDER.filter((c) => map[c] && map[c].length).map((c) => ({
      title: c,
      subtitle: CATEGORY_SUBTITLES[c],
      episodes: map[c],
    }));
  }, [episodes]);

  const featured = episodes[0];

  const availableCategories = useMemo(() => {
    const set = new Set();
    episodes.forEach((e) => set.add(e.category));
    return ["Tout", ...CATEGORY_ORDER.filter((c) => set.has(c) && c !== "Tous les épisodes")];
  }, [episodes]);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q && activeCategory === "Tout") return null;
    return episodes.filter((e) => {
      const catMatch = activeCategory === "Tout" || e.category === activeCategory;
      const qMatch = !q ||
        e.title.toLowerCase().includes(q) ||
        (e.description || "").toLowerCase().includes(q);
      return catMatch && qMatch;
    });
  }, [episodes, query, activeCategory]);

  const isSearching = searchResults !== null;

  return (
    <div data-testid="page-podcasts" className="pb-20 jt-page-dark">
      {/* ============ HERO BANNER À LA UNE ============ */}
      {featured && (
        <section className="relative h-[75vh] min-h-[520px] w-full overflow-hidden">
          <img
            src={featured.image_url}
            alt={featured.title}
            className="absolute inset-0 w-full h-full object-cover jt-pan"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0806] via-[#0A0806]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0806] via-[#0A0806]/40 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex items-end pb-20">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#E63946] mb-4">
                <Flame className="w-3.5 h-3.5" /> À la une
              </span>
              <h1 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-wider text-white mb-5">
                {featured.title}
              </h1>
              <p className="text-white/75 max-w-xl line-clamp-3 mb-7 text-lg">{featured.description}</p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setSelected(featured)}
                  data-testid="featured-play"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#E63946] text-white hover:bg-[#C1121F] transition jt-red-glow"
                >
                  <Play className="w-4 h-4" fill="currentColor" /> Écouter maintenant
                </button>
                <a
                  href={PLATFORM_LINKS.spotify}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/10 backdrop-blur border border-white/25 text-white hover:bg-white/20 transition"
                >
                  <ExternalLink className="w-4 h-4" /> Sur Spotify
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ LES 4 ÉMISSIONS ============ */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-4">
        <div className="mb-10">
          <span className="text-xs uppercase tracking-[0.3em] text-[#F4A94E]">Nos programmes</span>
          <h2 className="font-display text-4xl md:text-5xl tracking-wider text-white mt-3">
            4 ÉMISSIONS, 4 UNIVERS
          </h2>
        </div>
        <ShowsGrid dark />
      </section>

      {/* ============ SEARCH ============ */}
      {!loading && !error && (
        <div className="max-w-7xl mx-auto px-6 pt-14" data-testid="podcasts-search-bar">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                data-testid="search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un épisode, un thème, un verset…"
                className="w-full pl-11 pr-10 py-3 rounded-full bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 focus:ring-2 focus:ring-[#E63946] focus:border-[#E63946] transition"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  data-testid="search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
                  aria-label="Effacer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 md:ml-auto" data-testid="category-filters">
              {availableCategories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors border ${
                    activeCategory === c
                      ? "bg-[#E63946] text-white border-[#E63946]"
                      : "bg-white/5 text-white/80 border-white/10 hover:border-white/40"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ============ ROWS OR SEARCH ============ */}
      <div className="max-w-[100rem] mx-auto pt-6 md:pt-10">
        {loading && (
          <div className="flex items-center justify-center py-24 text-white">
            <Loader2 className="w-6 h-6 animate-spin mr-3" />
            <span className="font-serif text-xl">Chargement des épisodes…</span>
          </div>
        )}
        {error && !loading && (
          <div className="text-center py-24 text-[#E63946]" data-testid="podcasts-error">{error}</div>
        )}
        {!loading && !error && isSearching && (
          <section className="px-6 pb-16" data-testid="search-results">
            <div className="max-w-7xl mx-auto">
              <h3 className="font-display text-3xl md:text-4xl tracking-wider text-white mb-1">
                {searchResults.length} RÉSULTAT{searchResults.length !== 1 ? "S" : ""}
              </h3>
              <p className="text-sm text-white/60 mb-8">
                {query && <>Recherche : <em>« {query} »</em></>}
                {query && activeCategory !== "Tout" && " · "}
                {activeCategory !== "Tout" && <>Catégorie : {activeCategory}</>}
              </p>
              {searchResults.length === 0 ? (
                <div className="py-16 text-center text-white/60">
                  Aucun épisode trouvé. Essaie un autre mot-clé.
                </div>
              ) : (
                <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {searchResults.map((ep) => (
                    <EpisodeCard key={ep.id} episode={ep} onPlay={setSelected} dark />
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
        {!loading && !error && !isSearching && rows.map((r) => (
          <PodcastRow
            key={r.title}
            title={r.title}
            subtitle={r.subtitle}
            episodes={r.episodes}
            onPlay={setSelected}
            dark
          />
        ))}
      </div>

      {/* Player Modal */}
      {selected && <PlayerModal episode={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function PlayerModal({ episode, onClose }) {
  return (
    <div
      data-testid="player-modal"
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-[#14100B] border border-white/10 rounded-3xl w-full max-w-2xl overflow-hidden jt-medium-shadow text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="player-close"
          aria-label="Fermer"
          className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white z-10"
        >
          <X className="w-5 h-5" />
        </button>
        {episode.image_url && (
          <div className="relative h-52 md:h-64">
            <img src={episode.image_url} alt={episode.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#14100B] to-transparent" />
          </div>
        )}
        <div className="p-6 md:p-8">
          <h3 className="font-serif text-2xl md:text-3xl text-white leading-tight mb-3">{episode.title}</h3>
          <p className="text-sm text-white/70 line-clamp-4 mb-5">{episode.description}</p>
          {episode.audio_url ? (
            <audio controls autoPlay className="w-full" src={episode.audio_url} data-testid="player-audio">
              Ton navigateur ne supporte pas l'audio.
            </audio>
          ) : (
            <p className="text-sm text-[#E63946]">Aucun lien audio disponible.</p>
          )}
          <div className="flex flex-wrap gap-2 mt-5">
            {episode.link && (
              <a
                href={episode.link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-white text-sm hover:bg-white/10"
              >
                <ExternalLink className="w-4 h-4" /> Voir l'épisode
              </a>
            )}
            <ShareEpisode episode={episode} variant="pill" dark />
          </div>
        </div>
      </div>
    </div>
  );
}
